/* eslint-disable max-lines-per-function */
import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPublicUrl } from 'reactApp/appHelpers/appHelpers';
import { unPublishHelper } from 'reactApp/appHelpers/publishHelper';
import { SharingNewSection } from 'reactApp/components/SharingNew/Section/SharingNewSection';
import { sendGa } from 'reactApp/components/SharingNew/SharingNew.helpers';
import { EFrom } from 'reactApp/components/SharingNew/SharingNew.types';
import { AutoDelete } from 'reactApp/components/SharingNew/Weblink/AutoDelete/AutoDelete';
import { getFeaturePublicAutoDeletePromo, getFeaturePublicDomestic } from 'reactApp/modules/features/features.selectors';
import { publishRequest } from 'reactApp/modules/modifying/modifying.actions';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isAutoDeleteEnabled } from 'reactApp/modules/sharedAutoDelete/sharedAutoDelete.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';

import { useAccessRights } from './hooks/useAccessRights';
import { useDomestic } from './hooks/useDomestic';
import { useExpires } from './hooks/useExpires';
import styles from './SharingNewWeblink.css';
import { renderUnpublishWeblinkDialog } from './SharingNewWeblink.helpers';
import { IProps } from './SharingNewWeblink.types';

export const SharingNewWeblink = memo(function SharingNewWeblink(props: IProps): ReactElement | null {
    const { item, from, isSharedOrMouted, gaSuffix, noSwitch = false, noExpiresControl = false, isPhone } = props;
    const dispatch = useDispatch();
    const publicUrl = getPublicUrl(item);
    const isPublic = Boolean(item?.weblink);
    const storage = useSelector(getCurrentStorage);
    // Если пользователь пришел с "Поделиться", то блок с паблик-линком раскрыт,
    // Блок так же раскрыт, если есть паблик-ссылка
    const isFromWeblink = from === EFrom.WEBLINK || isPublic;
    const isAlbum = from === EFrom.ALBUMS || storage === EStorageType.albums;

    const [isWeblinkOpened, setWeblinkOpened] = useState<boolean>(isFromWeblink);

    const renderExpires = useExpires(item);
    const renderDomestic = useDomestic(item);
    const renderAccessRights = useAccessRights(item, isSharedOrMouted);

    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isDomesticFeatureEnabled = useSelector(getFeaturePublicDomestic);
    const showDomesticSettings = !isAlbum && (isBizUser || isCorpUser) && Boolean(isDomesticFeatureEnabled);
    const isAutodeleteFeatureEnabled = Boolean(useSelector(isAutoDeleteEnabled));
    const isAutodeletePromoFeatureEnabled = Boolean(useSelector(getFeaturePublicAutoDeletePromo));
    const showExpires = !isAlbum && !noExpiresControl;
    const showAccessRights = !isAlbum;
    const showAutoDeleteSettings =
        !isAlbum && item?.isFolder && !isSharedOrMouted.isMounted && (isAutodeleteFeatureEnabled || isAutodeletePromoFeatureEnabled);

    const onActionClick = useCallback(() => {
        sendGa('unpublish');
        if (storage === EStorageType.attaches) {
            sendGa('attaches-unpublish');
        }

        if (!item) {
            return;
        }

        unPublishHelper({ items: [item] });
    }, [storage, item]);

    const onChangeWeblink = useCallback(() => {
        if (!item) {
            return;
        }

        if (isWeblinkOpened) {
            renderUnpublishWeblinkDialog({ onActionClick, item, isAlbum });
        } else {
            sendGa('publish');

            dispatch(publishRequest({ item }));
        }
    }, [isWeblinkOpened, item, onActionClick]);

    useEffect(() => {
        setWeblinkOpened(Boolean(publicUrl));
    }, [publicUrl]);

    return (
        <SharingNewSection
            onChange={onChangeWeblink}
            isOpened={isWeblinkOpened}
            title="По ссылке"
            name="weblink"
            noSwitch={noSwitch}
            isPhone={isPhone}
        >
            <div className={classNames(styles.root, { [styles.root_phone]: isPhone })}>
                <div className={styles.copyWeblink}>
                    <CopyWeblink type="new" publicUrl={publicUrl} autoCopy={!isPhone} isPhone={isPhone} />
                </div>

                {showDomesticSettings && renderDomestic()}
                {showAccessRights && renderAccessRights()}
                {showExpires && renderExpires()}
                {showAutoDeleteSettings && (
                    <AutoDelete
                        item={item}
                        disabled={!isAutodeleteFeatureEnabled && isAutodeletePromoFeatureEnabled && isWeblinkOpened}
                        gaSuffix={gaSuffix}
                    />
                )}
            </div>
        </SharingNewSection>
    );
});
